import React, { useEffect, useMemo } from 'react';
import produce from 'immer';
import {
  type CustomButton,
  CustomButtonType,
  type CustomFeature,
  FeatureType,
  ImageType,
} from '@types';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import Tip from '@components/Tip';
import Button from '@components/Button';
import { $editingCustomButton } from '@stores/editingStores';
import useTemplateFeature from '@hooks/useTemplateFeature';
import FeatureIcon from '@components/FeatureIcon';
import PanelBase from '../PanelBase';
import { customButtonDefault } from '../../edit';
import { selectLibCBIcons } from '../../mediaLib';
import { type CFSetData, CustomFeatureProvider } from '../CustomFeatureContext';
import CButtonTitle from '../customFeatureComponents/CButtonTitle';
import FeatureSwitch from '../components/FeatureSwitch';
import CSceneSetting from '../customFeatureComponents/CSceneSetting';
import type { PanelBaseProps } from '../types';
import useCustomButtonEditing from './useCustomButtonEditing';
import CNextSceneSetting from '../customFeatureComponents/CNextSceneSetting';

interface Props extends PanelBaseProps<CustomButton[]> {
  groupIndex: number;
  buttonIndex: number;
  isNew?: boolean;
  batchMode?: boolean;
}

const items: DropdownItem<FeatureType>[] = [
  { id: FeatureType.link, name: '超链接' },
  { id: FeatureType.mobile, name: '电话' },
  { id: FeatureType.map, name: '导航' },
  { id: FeatureType.media, name: '图文' },
  { id: FeatureType.text, name: '文本' },
  { id: FeatureType.article, name: '文章' },
  { id: FeatureType.doc, name: 'PDF' },
  { id: FeatureType.audio, name: '音频' },
  { id: FeatureType.video, name: '视频' },
  { id: FeatureType.miniProgram, name: '小程序' },
  { id: FeatureType.model3d, name: '3D模型' },
  { id: FeatureType.seqFrame, name: '逐帧环物' },
  { id: FeatureType.scene, name: '场景切换' },
  { id: FeatureType.prevScene, name: '上一场景' },
  { id: FeatureType.nextScene, name: '下一场景' },
  { id: FeatureType.event, name: '事件' },
];

function CustomButtonBase({
  defaultValue,
  onConfirm,
  groupIndex,
  buttonIndex,
  isNew,
  batchMode,
}: Props) {
  const { state, setState, isChanged } = useCustomButtonEditing(defaultValue);
  const group = state[groupIndex];
  const customButtonIcon = useTemplateFeature('customButtonIcon');
  const dropdownItems = useMemo(
    () => (batchMode ? items.filter((x) => x.id !== FeatureType.scene) : items),
    [batchMode]
  );

  useEffect(() => {
    $editingCustomButton.api.set(state);
    return () => {
      $editingCustomButton.api.set(null);
    };
  }, [state]);

  const item = group.children[buttonIndex];
  const setItem: CFSetData<CustomFeature> = (cb) => {
    setState((draft) => {
      cb(draft[groupIndex].children[buttonIndex]);
    });
  };

  const showButton = group.type === CustomButtonType.button && customButtonIcon;

  const handleDelete = () => {
    if (group.type === CustomButtonType.button) {
      onConfirm(
        produce(state, (draft) => {
          draft.splice(groupIndex, 1);
        })
      );
    } else if (group.type === CustomButtonType.group) {
      onConfirm(
        produce(state, (draft) => {
          draft[groupIndex].children.splice(buttonIndex, 1);
        })
      );
    }
  };

  const handleConfirm = () => {
    onConfirm(state);
  };

  const handleAfterClose = () => {
    if (isNew) handleDelete();
  };

  return (
    <PanelBase
      title={<Title>按钮设置</Title>}
      isChanged={isChanged}
      onDeleteClick={handleDelete}
      onConfirm={handleConfirm}
      onAfterClose={handleAfterClose}
    >
      <CustomFeatureProvider setData={setItem} data={item}>
        <Collapse title="功能选择">
          <Dropdown
            items={dropdownItems}
            selected={item.code}
            onChange={(id) =>
              setState((draft) => {
                draft[groupIndex].children[buttonIndex] =
                  customButtonDefault(id);
              })
            }
          />
        </Collapse>
        {showButton && (
          <Collapse title="图标设置">
            <ImageContainer
              className="h-70"
              useResetIcon
              placeholder={
                <FeatureIcon type={item.code} className="h-40 w-40" />
              }
              onDeleteClick={() =>
                setState((draft) => {
                  draft[groupIndex].iconMediaId = 0;
                  draft[groupIndex].iconMediaUrl = '';
                })
              }
            >
              {!!group.iconMediaUrl && (
                <Image
                  className="h-40 w-40"
                  src={group.iconMediaUrl}
                  type={
                    group.iconMediaId === 0
                      ? ImageType.sysMedia
                      : ImageType.media
                  }
                />
              )}
            </ImageContainer>
            <div className="mt-10 flex items-center justify-between">
              <Tip>建议尺寸：80 X 80</Tip>
              <Button
                size="sm"
                onClick={async () => {
                  const { libItems, sysItems } = await selectLibCBIcons();
                  if (libItems) {
                    setState((draft) => {
                      draft[groupIndex].iconMediaId = libItems[0].id;
                      draft[groupIndex].iconMediaUrl = libItems[0].url;
                    });
                  } else if (sysItems) {
                    setState((draft) => {
                      draft[groupIndex].iconMediaId = 0;
                      draft[groupIndex].iconMediaUrl = sysItems[0].url;
                    });
                  }
                }}
              >
                编辑图标
              </Button>
            </div>
          </Collapse>
        )}
        <CButtonTitle />
        <FeatureSwitch code={item.code} />
        {item.code === FeatureType.scene && <CSceneSetting />}
        {item.code === FeatureType.prevScene && <CNextSceneSetting reverse />}
        {item.code === FeatureType.nextScene && <CNextSceneSetting />}
      </CustomFeatureProvider>
    </PanelBase>
  );
}

export default CustomButtonBase;
